'use client'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { parseAsString, useQueryState } from 'nuqs'
import { FIELD_HIGHLIGHT_PARAM } from '@/constants/profileCompleteness'
import { FieldHighlightContextProvider } from './context'

/**
 * Budgeted in frames, not wall-clock. The poll runs on `requestAnimationFrame`,
 * which is paused while the tab is in the background — and these links are
 * opened from WhatsApp and email, so landing in a background tab is the normal
 * case. A wall-clock deadline would expire unseen and consume the param, so the
 * field would never be marked once the viewer finally looked at it. Roughly
 * three seconds of *visible* time at 60fps.
 */
const LOOKUP_FRAME_BUDGET = 180
const HIGHLIGHT_DURATION_MS = 2500

/**
 * Points the viewer at one field named in the URL: `?highlight=<field name>`.
 *
 * It lives in the shell rather than in a screen, so it costs nothing to reuse:
 * the shared inputs match it against their own `name`, and every control
 * already renders `id={name}`, so aiming a later prompt at a different field is
 * a URL change rather than code. The mark itself is the inputs' existing
 * warning state — nothing new to style, and nothing to clean up by hand.
 */
export const FieldHighlightProvider = ({ children }: { children: React.ReactNode }) => {
  const [field, setField] = useQueryState(FIELD_HIGHLIGHT_PARAM, parseAsString.withDefault(''))
  const [highlighted, setHighlighted] = useState('')
  // Outlives the effect on purpose: consuming the param re-runs it, and a
  // cleanup that cancelled this timer left the field marked for good.
  const clearHighlightRef = useRef(0)

  useEffect(() => () => window.clearTimeout(clearHighlightRef.current), [])

  const reveal = useCallback((name: string) => {
    const control = document.getElementById(name)
    if (!control) return false

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    control.scrollIntoView({ block: 'center', behavior: prefersReducedMotion ? 'auto' : 'smooth' })
    control.focus({ preventScroll: true })
    setHighlighted(name)
    window.clearTimeout(clearHighlightRef.current)
    clearHighlightRef.current = window.setTimeout(() => setHighlighted(''), HIGHLIGHT_DURATION_MS)
    return true
  }, [])

  useEffect(() => {
    // Consuming the param is async, so this effect can run again while `field`
    // still holds the name it just handled — without this guard the mark would
    // be set twice and the first timer would cut the second one short.
    if (!field || highlighted === field) return

    let frame = 0
    let attemptsLeft = LOOKUP_FRAME_BUDGET
    // The target mounts only once its section renders, so this polls rather
    // than measuring once; it stops at the first hit or when the budget runs out.
    const attempt = () => {
      attemptsLeft -= 1
      if (reveal(field) || attemptsLeft <= 0) {
        // Consumed: a reload or a shared link must not re-announce the field.
        setField(null)
        return
      }
      frame = requestAnimationFrame(attempt)
    }

    frame = requestAnimationFrame(attempt)

    return () => cancelAnimationFrame(frame)
  }, [field, highlighted, setField, reveal])

  return (
    <FieldHighlightContextProvider value={highlighted}>
      {children}
    </FieldHighlightContextProvider>
  )
}

export { useHighlightedField } from './context'
